import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventEmitter } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { WorkflowUtilitiesService } from '../services/workflow-utilities.service';

@Injectable({
	providedIn: 'root'
})
export class TaskService {

	// EventEmitter used to replace AngularJS $broadcast (eg. $rootScope.$broadcast()), "dxTaskEvent"
	workflowTaskEvent: EventEmitter<{ eventType: string, timestamp: Date }> = new EventEmitter();

	constructor(
		private dxUtilsService: WorkflowUtilitiesService,
		private configService: ConfigService,
		private http: HttpClient
	) { }

	/**
	 * Returns the tasks owned by the user.
	 */
	async getTasksForUser(user: string, page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/assigned/user/${user}`;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let tasks = response.content;
				tasks.forEach((task: any) => {
					this.formatTask(task);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	/**
	 * Returns the tasks the user is qualified to own.
	 */
	async getPotentialTasksForUser(user: string, page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/assignable/user/${user}`;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let tasks = response.content;
				tasks.forEach((task: any) => {
					this.formatTask(task);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getAllTasksFilteredBy(filter: any, page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;

		if ('userOwner' == filter.id) {
			url += `/assigned/user/${filter.value}`;
		} else if ('groupOwner' == filter.id) {
			// TODO
		} else if ('userAssignable' == filter.id) {
			url += `/assignable/user/${filter.value}`;
		} else if ('groupAssignable' == filter.id) {
			url += `/assignable/group/${filter.value}`;
		} else if ('batch' == filter.id) {
			url += `/batch/name/${filter.value}`;
		} else if ('status' == filter.id) {
			url += `/status/${filter.value}`;
		}

		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let tasks = response.content;
				tasks.forEach((task: any) => {
					this.formatTask(task);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getTask(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/${id}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response) {
				let task = response;
				this.formatTask(task);
				return task;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massClaim(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/mass/claim`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'claim',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massClaimForBatch(batchUUID: string, taskId: number, payload: any): Promise<any> {
		let url = `/aw-server/rest/miscworkflow/task/mass/claim`;
		url += `/batch/${batchUUID}`;
		url += `/task/${taskId}`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'claim',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massAssign(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/mass/assign`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'assign',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massRelease(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/mass/release`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'release',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massComplete(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/mass/complete`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'complete',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massReject(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/mass/reject`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowTaskEvent.emit({
				eventType: 'reject',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async saveTaskData(id: number, payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/id/${id}/taskdata/save`;

		try {
			return await firstValueFrom(this.http.post<any>(url, payload));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getTaskData(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/${id}/taskdata`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response) {
				//	    			response.data.data = JSON.parse(response.data.data);
				//	    			deferred.resolve(response.data);				
				response.data = JSON.parse(response.data);
				return response.data;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async startTask(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/${id}/start`;

		try {
			const response = firstValueFrom(await this.http.post<any>(url, {}));
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async stopTask(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.tasksURL;
		url += `/${id}/stop`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, {}));
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async saveTaskAnnotationSession(uuid: string, taskId: number, annotations: any): Promise<any> {
		let baseAnnotationURL = this.configService.getConfig().dx.baseAnnotationURL;
		baseAnnotationURL += `/persist/${uuid}?task=${taskId}`;

		try {
			const response = await firstValueFrom(this.http.put<any>(baseAnnotationURL, annotations));
			// Update the task with the new session id
			let annotationSessionId = response.annotationSessionId;

			let tasksURL = this.configService.getConfig().dx.tasksURL;
			tasksURL += `/${taskId}/sid/${annotationSessionId}`;
			try {
				return await firstValueFrom(this.http.post<any>(tasksURL, {}));
			} catch (error) {
				return Promise.reject(error);
			}

		} catch (error) {
			return Promise.reject(error);
		}
	}

	private formatTask(task: any) {
		if (!task) return;
		if (task.dueDate) {
			var due = new Date(task.dueDate);
			task.dueDateStr = due.toLocaleDateString('en-US') + ' ' + due.toLocaleTimeString('en-US');
		}
		if (task.createdDate) {
			var created = new Date(task.createdDate);
			task.createdDateStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
		}
		if (task.expirationDate) {
			var expires = new Date(task.expirationDate);
			task.expirationDateStr = expires.toLocaleDateString('en-US') + ' ' + expires.toLocaleTimeString('en-US');
		}
		if (task.lastOpenedDate) {
			var lastOpenedDate = new Date(task.lastOpenedDate);
			task.lastOpenedDateStr = lastOpenedDate.toLocaleDateString('en-US') + ' ' + lastOpenedDate.toLocaleTimeString('en-US');
		}
		var task_comments: any[] = [];
		if (task.comments && task.comments.comments && task.comments.comments.length > 0) {
			task.comments.comments.forEach((c: any) => {
				var created = new Date(c.commentDate);
				c.commentDateStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
				task_comments.push(c);
			});
			delete task.comments;
			task.comments = task_comments;
		}

		// Custom metadata processing
		task.productMetadataDisplay = [];
		let metadataFields: { [key: string]: any } = this.dxUtilsService.getDomainCustomization("metadata") || {};
		let metadataFieldsDisplay = metadataFields['display'] || [];
		var metadataValues = task.productMetadata || {};
		metadataFieldsDisplay.forEach((f: any) => {
			var fieldvalue = metadataValues[f.field];
			if (fieldvalue && fieldvalue.length > 0) {
				task.productMetadataDisplay.push({ field: f.field, label: f.label, value: fieldvalue.join(', '), isClaimLink: f.isClaimLink });
			} else {
				task.productMetadataDisplay.push({ field: f.field, label: f.label, value: "", isClaimLink: f.isClaimLink });
			}
		});
		// END Custom metadata processing    	

		// Determine the available task actions based on current task status
		task.actions = {};
		if ('Completed' == task.jbpmStatus ||
			'Failed' == task.jbpmStatus ||
			'Error' == task.jbpmStatus ||
			'Exited' == task.jbpmStatus ||
			'Obsolete' == task.jbpmStatus) {
			// task is already done
			task.actions.canClaim = false;
			task.actions.canAssign = false;
			task.actions.canComplete = false;
			task.actions.canReject = false;
			task.actions.canRelease = false;
		} else {
			if ('Ready' == task.jbpmStatus) {
				task.actions.canClaim = true;
				task.actions.canAssign = true;
				task.actions.canComplete = true;
				if (task.rejectDisabled) {
					task.actions.canReject = false;
				} else {
					task.actions.canReject = task.state != 'REJECTED' ? true : false;
				}
			} else if ('Reserved' == task.jbpmStatus) {
				task.actions.canAssign = true;
				task.actions.canComplete = true;
				if (task.rejectDisabled) {
					task.actions.canReject = false;
				} else {
					task.actions.canReject = true;
				}
				task.actions.canRelease = true;
			} else if ('InProgress' == task.jbpmStatus) {
				task.actions.canComplete = true;
				if (task.rejectDisabled) {
					task.actions.canReject = false;
				} else {
					task.actions.canReject = task.state != 'REJECTED' ? true : false;
				}
			}
		}
	}

	resetDirtyEditFlag(): void {
		this.workflowTaskEvent.emit({
			eventType: 'clearDirtyEditorFlag',
			timestamp: new Date(),
		});
	}

	/*
	 * Deprecated (AFDO pilot) 
	 * Returns a SF715 decision based on the provided decision data 
	 */
	//	    svc.getSF715TaskDecision = function(payload){
	//           var customize = dxUtilsService.getCustomization();	
	//	        var deferred = $q.defer();
	//	        $http({
	//	            method: "POST",
	//	            url: configService.getConfig().dx.tasksURL + "/decision/sf715",
	//	            data: payload
	//	    	}).then(function(response){
	//	    		if(response.data){
	//	    			var decisionData = response.data;    			
	//	    			var taskDataForm = customize.taskDataForm ? customize.taskDataForm.name : null;
	//	    			if ('sf715TaskData' == taskDataForm) {
	//	    				decisionData = {
	//	    						declassify: response.data.decision == 'Declassify',
	//	    						exempt:  response.data.decision == 'Exempt',
	//	    						exclude:  response.data.decision == 'Exclude',
	//	    						refer:  response.data.decision == 'Refer',
	//	    						exemptReason: response.data.exemptReason,
	//	    						excludeReason: response.data.excludeReason,
	//	    						referto: response.data.referrals,
	//	    						classificationChangeTo: response.data.classificationChange,
	//	    						justification: response.data.justification
	//	    				};
	//	    			}
	//	    			else if ('afdoTaskData' == taskDataForm) {
	//	    				decisionData = {
	//	    						decision: response.data.decision,
	//	    						reason: response.data.reason,
	//	    						referto: response.data.referrals,
	//	    						justification: response.data.justification
	//	    				};
	//	    			}
	//	    			deferred.resolve(decisionData);
	//	    		}else{
	//	    			deferred.reject(false);
	//	    		}            
	//	    	}, function(){
	//	    		deferred.reject(false);
	//	    	});
	//	    	return deferred.promise;     	
	//	    } 
}
